// Canyon Storm — Dawnbreakers variant.
//
// Dawnbreakers is played jointly with a partner alliance. FoA commits to one
// side of the map before the event (left = DC I / DS I / SF I / SW I+II,
// right = DC II / DS II / SF II / SW III+IV). Power Tower and Virus Lab are
// contested jointly regardless of side.
//
// The variant is defined in an abstract "own-side" slot space so a user-saved
// override doesn't have to think about left vs. right:
//   dataCenterOwn, defenseSystemOwn, serumFactoryOwn,
//   warehouseOwnA, warehouseOwnB, powerTower, virusLab
// At build time these expand to the 12 concrete `PlanSlotKey`s the renderer
// expects; the opposite-side slots receive capacity 0 / priority null.
//
// See docs/plans/012-dawnbreakers-cs.md for the planning model.

import {
  NON_VIRUS_SLOTS,
  type CSPlanConfig,
  type NonVirusSlot,
  type PhaseDef,
  type PlanSlotKey,
  type Priority,
  type VariantMeta,
} from './types';

export type DawnbreakersSide = 'left' | 'right';

const SIDE_LABEL: Record<DawnbreakersSide, string> = {
  left: 'Left',
  right: 'Right',
};

// Own-side abstract slot keys. See file header.
export const OWN_SLOT_KEYS = [
  'dataCenterOwn',
  'defenseSystemOwn',
  'serumFactoryOwn',
  'warehouseOwnA',
  'warehouseOwnB',
  'powerTower',
  'virusLab',
] as const;
export type OwnSideSlotKey = (typeof OWN_SLOT_KEYS)[number];
export type OwnSideNonVirusSlot = Exclude<OwnSideSlotKey, 'virusLab'>;

export const OWN_NON_VIRUS_SLOTS: OwnSideNonVirusSlot[] = OWN_SLOT_KEYS.filter(
  (k) => k !== 'virusLab',
) as OwnSideNonVirusSlot[];

// Own → concrete PlanSlotKey per side.
const OWN_TO_SIDE: Record<DawnbreakersSide, Record<OwnSideSlotKey, PlanSlotKey>> = {
  left: {
    dataCenterOwn: 'dataCenter1',
    defenseSystemOwn: 'defenseSystem1',
    serumFactoryOwn: 'serumFactory1',
    warehouseOwnA: 'warehouse1',
    warehouseOwnB: 'warehouse2',
    powerTower: 'powerTower',
    virusLab: 'virusLab',
  },
  right: {
    dataCenterOwn: 'dataCenter2',
    defenseSystemOwn: 'defenseSystem2',
    serumFactoryOwn: 'serumFactory2',
    warehouseOwnA: 'warehouse3',
    warehouseOwnB: 'warehouse4',
    powerTower: 'powerTower',
    virusLab: 'virusLab',
  },
};

function expand(side: DawnbreakersSide, slot: OwnSideSlotKey): PlanSlotKey {
  return OWN_TO_SIDE[side][slot];
}

// ---- Own-side canonical definitions ----

export type OwnPhaseConfig = {
  capacities: Record<OwnSideSlotKey, number>;
  strengthClaims?: { slot: OwnSideSlotKey; count: number }[];
};

// Own-side editable core. This is what a user override serializes to (plus
// homeByRank + ptCoreRanks). Everything else — phase copy, priorities —
// stays static and gets merged back in at expand time.
export type DawnbreakersOwnConfig = {
  phases: OwnPhaseConfig[]; // 4 entries, matches phase order below
  homeByRank: OwnSideNonVirusSlot[];
  ptCoreRanks: readonly number[];
};

const HOME_OWN: OwnSideNonVirusSlot[] = [
  'powerTower',        // rank 0  (PT core)
  'powerTower',        // rank 1  (PT core)
  'powerTower',        // rank 2  (PT core)
  'powerTower',        // rank 3  (PT core)
  'dataCenterOwn',     // rank 4
  'dataCenterOwn',     // rank 5
  'dataCenterOwn',     // rank 6
  'dataCenterOwn',     // rank 7
  'defenseSystemOwn',  // rank 8
  'defenseSystemOwn',  // rank 9
  'defenseSystemOwn',  // rank 10
  'defenseSystemOwn',  // rank 11
  'warehouseOwnA',     // rank 12
  'warehouseOwnA',     // rank 13
  'warehouseOwnA',     // rank 14
  'warehouseOwnB',     // rank 15
  'warehouseOwnB',     // rank 16
  'warehouseOwnB',     // rank 17
];

const PT_CORE_RANKS = [0, 1, 2, 3] as const;

// Priorities are identical across sides; opposite-side keys always null.
const PRIORITIES_OWN: Record<
  'phase1' | 'phase2' | 'phase3' | 'phase4',
  Record<OwnSideSlotKey, Priority | null>
> = {
  phase1: {
    dataCenterOwn: 'high',
    powerTower: 'critical',
    serumFactoryOwn: null,
    defenseSystemOwn: null,
    virusLab: 'critical',
    warehouseOwnA: 'lower',
    warehouseOwnB: 'lower',
  },
  phase2: {
    dataCenterOwn: 'high',
    powerTower: 'critical',
    serumFactoryOwn: null,
    defenseSystemOwn: 'high',
    virusLab: 'critical',
    warehouseOwnA: 'lower',
    warehouseOwnB: 'lower',
  },
  phase3: {
    dataCenterOwn: 'high',
    powerTower: 'critical',
    serumFactoryOwn: 'medium',
    defenseSystemOwn: 'high',
    virusLab: 'critical',
    warehouseOwnA: 'lower',
    warehouseOwnB: 'lower',
  },
  phase4: {
    dataCenterOwn: 'high',
    powerTower: 'critical',
    serumFactoryOwn: 'medium',
    defenseSystemOwn: 'high',
    virusLab: 'critical',
    warehouseOwnA: 'lower',
    warehouseOwnB: 'lower',
  },
};

const DEFAULT_OWN_PHASES: OwnPhaseConfig[] = [
  {
    capacities: {
      dataCenterOwn: 6,
      powerTower: 6,
      serumFactoryOwn: 0,
      defenseSystemOwn: 0,
      virusLab: 2,
      warehouseOwnA: 3,
      warehouseOwnB: 3,
    },
    // Force top-2 (flex) onto VL and next 6 (ranks 0–5) onto PT so PT holds
    // 6 in Ph1 even though the PT home only has 4 slots.
    strengthClaims: [
      { slot: 'virusLab', count: 2 },
      { slot: 'powerTower', count: 6 },
    ],
  },
  {
    capacities: {
      dataCenterOwn: 4,
      powerTower: 5,
      serumFactoryOwn: 0,
      defenseSystemOwn: 4,
      virusLab: 1,
      warehouseOwnA: 3,
      warehouseOwnB: 3,
    },
  },
  {
    capacities: {
      dataCenterOwn: 4,
      powerTower: 5,
      serumFactoryOwn: 4,
      defenseSystemOwn: 4,
      virusLab: 1,
      warehouseOwnA: 1,
      warehouseOwnB: 1,
    },
  },
  {
    capacities: {
      dataCenterOwn: 3,
      powerTower: 4,
      serumFactoryOwn: 2,
      defenseSystemOwn: 3,
      virusLab: 6,
      warehouseOwnA: 1,
      warehouseOwnB: 1,
    },
    strengthClaims: [
      { slot: 'virusLab', count: 6 },
      { slot: 'powerTower', count: 4 },
    ],
  },
];

export const DAWNBREAKERS_OWN_DEFAULT: DawnbreakersOwnConfig = {
  phases: DEFAULT_OWN_PHASES,
  homeByRank: HOME_OWN,
  ptCoreRanks: PT_CORE_RANKS,
};

const PHASE_COPY: Array<Pick<PhaseDef, 'title' | 'time' | 'timeColor' | 'subtitle' | 'subtitleColor'>> = [
  {
    title: 'PHASE 1',
    time: '0:00',
    timeColor: 'text-green-400',
    subtitle: 'START — DATA CENTER OPENS',
    subtitleColor: 'text-yellow-400',
  },
  {
    title: 'PHASE 2',
    time: '5:00',
    timeColor: 'text-yellow-400',
    subtitle: 'DEFENSE SYSTEM OPENS',
    subtitleColor: 'text-yellow-400',
  },
  {
    title: 'PHASE 3',
    time: '8:00',
    timeColor: 'text-orange-400',
    subtitle: 'SERUM FACTORY OPENS',
    subtitleColor: 'text-orange-400',
  },
  {
    title: 'PHASE 4',
    time: '12:00',
    timeColor: 'text-red-500',
    subtitle: 'VIRUS LAB OPENS — COLLAPSE & WIN',
    subtitleColor: 'text-red-500',
  },
];

function expandPriorities(
  side: DawnbreakersSide,
  own: Record<OwnSideSlotKey, Priority | null>,
): Record<PlanSlotKey, Priority | null> {
  const out: Partial<Record<PlanSlotKey, Priority | null>> = {};
  for (const k of Object.keys(own) as OwnSideSlotKey[]) out[expand(side, k)] = own[k];
  // Fill opposite-side slots with null so the shape stays complete.
  for (const s of NON_VIRUS_SLOTS) if (out[s] === undefined) out[s] = null;
  if (out.virusLab === undefined) out.virusLab = null;
  return out as Record<PlanSlotKey, Priority | null>;
}

function expandCapacities(
  side: DawnbreakersSide,
  own: Record<OwnSideSlotKey, number>,
): Record<PlanSlotKey, number> {
  const out: Partial<Record<PlanSlotKey, number>> = {};
  for (const k of Object.keys(own) as OwnSideSlotKey[]) out[expand(side, k)] = own[k];
  for (const s of NON_VIRUS_SLOTS) if (out[s] === undefined) out[s] = 0;
  if (out.virusLab === undefined) out.virusLab = 0;
  return out as Record<PlanSlotKey, number>;
}

function expandPhase(
  side: DawnbreakersSide,
  phaseIndex: number,
  own: OwnPhaseConfig,
): PhaseDef {
  const copy = PHASE_COPY[phaseIndex];
  const priKey = (['phase1', 'phase2', 'phase3', 'phase4'] as const)[phaseIndex];
  return {
    ...copy,
    priorities: expandPriorities(side, PRIORITIES_OWN[priKey]),
    capacities: expandCapacities(side, own.capacities),
    strengthClaims: own.strengthClaims?.map((c) => ({
      slot: expand(side, c.slot),
      count: c.count,
    })),
  };
}

function expandHomeByRank(
  side: DawnbreakersSide,
  own: OwnSideNonVirusSlot[],
): NonVirusSlot[] {
  return own.map((k) => expand(side, k) as NonVirusSlot);
}

function buildMeta(side: DawnbreakersSide): VariantMeta {
  return {
    key: 'dawnbreakers',
    name: `Dawnbreakers (${SIDE_LABEL[side]})`,
    shortDescription:
      'Joint play with a partner alliance. FoA covers one side of the map ' +
      '(left = DC I / DS I / SF I / SW I+II, right = the mirrored buildings). ' +
      'Power Tower and Virus Lab are contested jointly.',
    integratedAt: '2026-07-08',
    rulebookRef: 'docs/plans/012-dawnbreakers-cs.md',
    highlights: [
      'Phase 1: PT holds 6 (top 6 non-flex), DC 6, warehouses 3+3, VL 2 flex.',
      'Phase 2: DS opens with 4; PT shrinks to 4.',
      'Phase 3: SF opens with 4; warehouses shrink to 1 each.',
      'Phase 4: VL collapse to 6 (flex + PT core); PT 4, DS 3, DC 3, SF 2.',
      'Opposite side (DC II / DS II / SF II / SW III+IV) held by partner alliance.',
    ],
  };
}

export function buildDawnbreakersConfig(
  side: DawnbreakersSide,
  own: DawnbreakersOwnConfig = DAWNBREAKERS_OWN_DEFAULT,
): CSPlanConfig {
  return {
    meta: buildMeta(side),
    phases: own.phases.map((p, i) => expandPhase(side, i, p)),
    homeByRank: expandHomeByRank(side, own.homeByRank),
    ptCoreRanks: own.ptCoreRanks,
  };
}
